



























































import Vue, { PropType } from 'vue'
import Broadcast, { BroadcastTemplate } from '@/types/Broadcast.interface'
import Media from '@/types/Media.interface'
import modelsMixin from '@/mixins/models-mixin'
import moment from 'moment-timezone'
import ScheduleBuilder from '@/components/ScheduleBuilder.vue'
import BroadcastFormBanner from '@/components/BroadcastFormBanner.vue'
import BroadcastFormCompose from '@/components/BroadcastFormCompose.vue'
import BroadcastFormFooter from '@/components/BroadcastFormFooter.vue'
import BroadcastFormToolbar from '@/components/BroadcastFormToolbar.vue'
import BroadcastPhonePreview from '@/components/BroadcastPhonePreview.vue'
import { mapGetters } from 'vuex'
import { randomKey } from '@/utility/helpers'
export default Vue.extend({
    mixins: [modelsMixin],
    components: {
        BroadcastFormBanner,
        BroadcastFormCompose,
        BroadcastFormFooter,
        BroadcastFormToolbar,
        BroadcastPhonePreview,
        ScheduleBuilder
    },
    props: {
        //  The broadcast being updated or created
        broadcast: {
            type: Object as PropType<Broadcast>,
            required: true
        },
        //  Determines if we are editing a draft or not
        edit: {
            type: Boolean,
            default: false
        }
    },
    data: () => ({
        //  Auto populated name
        autoBroadcastName: null,
        //  Our key we want to assign to our broadcast form compose for rendering
        broadcastFormComposeKey: randomKey(),
        //  The message for our phone preview
        broadcastMessage: null,
        //  The media for our phone preview
        broadcastMedia: null,
        //  Determines whether we include company name in our message
        companyToggle: true,
        //  Dertermines whether we include consent message in our message
        consentToggle: true,
        //  Condiiton to monitor if a group has been selected
        groupSelected: null,
        //  Condition to monitor if broadcast has the company name in it or not 
        hasCompanyName: null, 
        //  Condition to monitor if broadcast has opt-out mesage in it or not 
        hasConsent: null,
        //  Sets the loading spinner
        loading: false,
        //  Conditional rendering for messagetextform valdiation
        messageEmpty: null,
        //  Starts the name transition
        nameTransition: null,
        //  Starts the name saved transition
        nameSaved: null,
        //  A temporary clone of the broadcast
        temp: null,
        //  Tracks validation for schedules bound to temp
        schedulesValid: false,
        //  Determines if we show the scheduling options
        showScheduler: false
    }),
    mounted() {
        //  Generate broadcast name with date
        this.autoBroadcastNameGenerate()

        //  Clone the original data (so we can revert changes if the user cancels the update)
        this.cloneBroadcast()

        //  Get some recent groups and persons for our search list
        this.$store.dispatch('groupsRecentPaginate')
        this.$store.dispatch('personsRecentPaginate')

        //  When creating a broadcast this will be null so we figure out if this is a new broadcast or a scheduled/draft one
        let msg = this.temp.message || ''

        //  We don't manage our opt-in and company name toggles if we're brand new broadcast, thus these default to being on 
        if (!msg) return

        //  Otherwise we check our message to see if it contains these items
        this.hasCompanyName = msg.includes(this.companyName)
        this.hasConsent = msg.includes(this.consentMsg)
        
        //  We turn the toggles off if they don't have the opt-out and/or company name as this means the user intentionally turned it off when they made their broadcast
        if (!this.hasCompanyName) this.companyToggle = false

        if (!this.hasConsent) this.consentToggle = false
    },
    computed: {
        ...mapGetters(['getEnabled', 'getLimit']),
        a2pEnabled(): boolean {
            //  Check if A2P is enabled
            return this.getEnabled('a2p')
        },
        // Function pulls from api to get the settings data table to see if a user is a2p enabled or not
        a2pRegistered(): boolean {
            const a2pEnabled = this.$store.getters.settingGet('a2pEnabled')

            //  If our setting doesn't exist, return false
            if (!a2pEnabled) return false

            //  Else, return based on if we are enabled or not
            return !!a2pEnabled.enabled
        },
        actionsDisabled(): boolean {
            //  Grab our temp
            const temp = this.temp

            //  If temp don't exist then return true
            if (!temp) return true

            //  If we don't have a message then we cannot send
            if (!temp.message) return true

            //  If we are scheduling and our schedules are not valid, we can't send
            if (this.broadcast.schedule && !this.schedulesValid) return true
        },
        broadcastEdited(): boolean {
            //  If temp is null then return false
            if (!this.temp) return false

            //  Our broadcast has been edited if ANY of our values differ from the default
            //  We can always change this if we don't want to be as granular
            const same = window._.isEqual(this.broadcast, this.temp)

            //  We return our condition
            return !same
        },
        defaultEdited(): boolean {
            //  If temp is null then return false
            if (!this.temp) return false

            //  If our default broadcast is null then return false
            if (!this.defaultBroadcast) return false

            //  Our broadcast has been edited if ANY of our values differ from the default
            //  We can always change this if we don't want to be as granular
            const same = window._.isEqual(this.defaultBroadcast, this.temp)

            //  We return our condition
            return !same
        },
        broadcastLimit(): number {
            //  Grab our limits
            return this.getLimit('broadcast') || 250
        },
        broadcastLimitReached(): boolean {
            //  If a2p is enabled, return false because we have no limits
            if (this.a2pEnabled) return false

            //  Else check the persons count
            const count = this.contactsCount

            //  Check if the count exceeds current limit
            return count > this.broadcastLimit
        },
        companyName(): string {
            //  Grab the current tenant from the store
            const tenant = this.$store.getters.tenant

            //  Get our tenant's organization name
            const orgName = tenant.company_name

            //  Return the orgname with a newline appended
            return `${orgName}\n`
        },
        consentMsg(): string {
            //  Grab our consent from the store
            const consent = this.$store.getters.consent

            //  Grab the opt in message
            const optIn = consent?.opt_in_message

            //  Return the opt in with a newline prepended
            return `\n${optIn}`
        },
        contactsCount(): number {
            return this.$store.getters.broadcastsViewSelectedBroadcastTotalCount
        },
        defaultBroadcast(): Broadcast {
            //  GRab the opt out group
            const opt = this.$store.getters.optOutGroup

            //  If we don't have the opt out yet, return null
            if (!opt) return null

            //  This is a default broadcast state, and lets us know if any values have been edited or not
            return {
                object: 'Broadcast',
                name: null,
                group_ids: [],
                groups: [],
                person_ids: [],
                persons: [],
                exclude_group_ids: [opt.id],
                exclude_groups: [opt],
                exclude_person_ids: [],
                exclude_persons: [],
                message: null,
                schedules: null
            }
        },
        displayBanner(): boolean {
            //  Check if we reached our broadcast limit
            const limitReached = this.broadcastLimitReached

            //  Check if we are trialing
            const trialing = this.trialing

            //  Check if we are NOT a2p registered
            const unregistered = !this.a2pRegistered

            //  If ANY of these are true, then we display
            return limitReached || trialing || unregistered
        },
        isApp(): boolean {
            return this.$store.getters.isApp
        },
        parsedBroadcastMessage(): string {
            //  Grab our broadcast message
            let msg = this.temp.message || ''
            
            //  We just return the message if the message is empty becuase if a user saves a draft without a message then we don't want to append the company name and consent message
            //  to this message, it causes issues if we do so with drafts. This does mean that a user who goes into a draft that doesn't have any text will not remember the toggle state 
            //  but I cannot find a way to handle this edgecase otherwise, I think they can live with having to press a toggle on a broadcast they didn't save a message for. This is quite the 
            //  edge case 
            if (!msg) {
                return msg
            }
            
            //  We check to see if the broadcast already contains these items, the reason is that we only check when the component is intitially loaded so this
            //  does subsequent checks and updates our values incase the user keeps changing the toggles
            this.hasCompanyName = msg.includes(this.companyName)
            this.hasConsent = msg.includes(this.consentMsg)
         
            //  If we have our company toggled and it doesn't have a company name yet we append it, this logic prevents a case where a draft or scheduled broadcast when editied appends additonal
            //  company names over and over again for each edit
            if (this.companyToggle && !this.hasCompanyName) {
                msg = `${this.companyName}${msg}`
            }
            //  If we have our consent message toggled and don't have our consent message in the broadcast, append it, same reasoning as above
            if (this.consentToggle && !this.hasConsent) {
                msg = `${msg}${this.consentMsg}`
            }

            //  In the case the user is editing their message from a schedule or draft broadcast we need the inverse logic of above so that the broadcast preview removes the text if they
            //  turn one of the toggles off
            if (!this.companyToggle && this.hasCompanyName) {
                msg = msg.replace(this.companyName, '')
            }
            if (!this.consentToggle && this.hasConsent) {
                msg = msg.replace(this.consentMsg, '')
            }

            //  Return our message
            return msg
        },
        toolbar() {
            //  Our title depends on if we are editing or not
            const title = this.edit ? `Editing ${this.broadcast.name}` : 'New Broadcast'

            return {
                title: title,
                left: [
                    {
                        title: 'Back',
                        icon: 'mdi-chevron-left',
                        back: '/broadcasts',
                        tooltip: 'Back',
                        permission: 0,
                        feature: null
                    }
                ],
                right: []
            }
        }
    },
    methods: {
        autoBroadcastNameGenerate(): void {
            //  Get the current date and time
            const now = moment.utc().local().format('YYYY-MM-DD hh:mm:ss')

            //  Save name
            this.autoBroadcastName = `New Broadcast ${now}`
        },
        cloneBroadcast(): void {
            //  Update our temp
            this.temp = window._.cloneDeep(this.broadcast)

            //  Set our message and media
            this.broadcastMessage = this.temp.message
            this.broadcastMedia = this.temp.media

            if (!this.temp.group_ids) this.temp.group_ids = []
            if (!this.temp.person_ids) this.temp.person_ids = []
            if (!this.temp.exclude_group_ids) this.temp.exclude_group_ids = []
            if (!this.temp.exclude_person_ids) this.temp.exclude_person_ids = []

            //  We should get the count whenever we clone
            this.$store.dispatch('broadcastsViewSelectedBroadcastTotalCount', this.temp)
        },
        onTemplateSelected(template: BroadcastTemplate): void {
            //  Check if our template includes media
            const media = template.media

            //  If it does, then append some help text
            const message = media
                ? template.message +
                  '\n(Click below: ⬇️ to add an image to your message!)'
                : template.message

            //  Assign the text from our template to our broadcast
            this.temp.message = message

            //  Update our phone preview
            this.broadcastMessage = message

            //  Re-render the form
            this.broadcastFormComposeKey = randomKey()

            //  We applied our template so make sure we unset it
            this.$store.commit('broadcastsViewSelectedTemplateSet', null)

            //  Get our toast to appear
            this.$store.commit('appToastSet', {
                text: 'Template Applied!',
                visible: true
            })

            //  If no media then we are done
            if (!media) return

            //  Else, give a hint
            this.$store.commit('broadcastsViewMediaHintSet', true)
        },
        //  Pop the broadcast preview to send a broadcast
        popBroadcastPreview(schedule: boolean = false): void {
            //  Before we open our preview, lets check if we have contacts
            const empty = this.contactsCount <= 0

            //  If we don't then do nothing
            if (empty) return this.popEmptyBroadcast()

            //  Grab our broadcast (clone to avoid referencing)
            const broadcast = window._.cloneDeep(this.temp)

            //  If our broadcast doesn't have a name then attach the auto
            if (!broadcast.name) broadcast.name = this.autoBroadcastName

            //  If we are scheduling, make sure to attach a schedule
            if (schedule) {
                broadcast.schedule = {
                    next_send: null,
                    interval: 'SEND ONCE'
                }
            }

            //  Set navigation path to broadcast index
            this.navigateAwayPath = '/broadcasts'

            //  Tell our parent about this path
            this.$emit('navigateAwaySet', '/broadcasts')

            //  Validate the send
            if (this.awaitingNumber) return this.popAwaitingNumber()

            //  Get the total number of contacts this broadcast gets sent to
            this.$store.dispatch('broadcastsViewSelectedBroadcastTotalCount', broadcast)

            //  Pop the broadcast preview
            this.$store.dispatch('dialogOpen', {
                type: 'APP',
                component: 'broadcast-preview',
                overflow: 'auto',
                closeable: true,
                props: {
                    broadcast: broadcast,
                    consentToggle: this.consentToggle,
                    companyToggle: this.companyToggle,
                    hasCompanyName: this.hasCompanyName,
                    hasConsent: this.hasConsent
                }
            })
        },
        popEmptyBroadcast(): void {
            this.$store.dispatch('dialogOpen', {
                type: 'MESSAGE',
                title: 'Broadcast is Empty',
                text: 'You are attempting to send a broadcast to 0 contacts. Please select at least 1 group containing contacts to proceed.',
                icon: 'mdi-alert',
                color: 'primary'
            })
        },
        popSaveDraft(route: string): void {
            //  Pop confirm dialog
            this.$store.dispatch('dialogOpen', {
                type: 'CONFIRM',
                title: 'Save as Draft?',
                icon: 'bi-box-arrow-in-down',
                color: 'primary',
                note: 'You are navigating away from creating a broadcast. Would you like to save your changes as a draft?',
                callback: async (createDraft: boolean) => {
                    //  If we don't want to then lets continue routing
                    if (!createDraft) return this.$emit('navigateAway')

                    //  Else, lets save our draft
                    const broadcast = await this.saveDraftBroadcast()

                    //  Create a goto path to bring us back once user's finished
                    const goto = `goto=/broadcasts/${broadcast.id}/edit`

                    //  Check if our route contains an existing query
                    const existing = route.includes('?')

                    //  If it does, then our append route needs to start with &, else it can be ?
                    const append = existing ? `&${goto}` : `?${goto}`

                    //  Append this to our route
                    const nav = `${route}${append}`

                    //  Route to our route
                    this.$router.push(nav)
                }
            })
        },
        popTestPreview(): void {
            this.$store.dispatch('dialogOpen', {
                type: 'APP',
                title: 'Test Broadcast Send',
                description:
                    'Send a single text message to yourself or another contact to test your Broadcast',
                icon: 'bi-send',
                color: 'primary',
                closeable: false,
                component: 'broadcast-message-test',
                props: {
                    broadcast: this.temp,
                    consentToggle: this.consentToggle,
                    companyToggle: this.companyToggle,
                    hasCompanyName: this.hasCompanyName,
                    hasConsent: this.hasConsent
                }
            })
        },
        //  Save the broadcast
        async saveBroadcast(): Promise<Broadcast> {
            //  Set loading spinner to true
            this.loading = true

            //  Grab temp
            const broadcast = this.temp

            //  Grab our media id
            const media_id = (broadcast.media && broadcast.media.id) || null

            //  Remove media from the broadcast
            delete broadcast.media

            //  Attach media id
            broadcast.media_id = media_id

            //  Update our broadcast's text with our parsed value
            broadcast.message = this.parsedBroadcastMessage

            //  Send the request
            const response = await this.$store.dispatch('broadcastsPut', broadcast)

            //  If the request failed, do nothing else
            if (!response) return null

            //  Notify the user of success
            this.$store.commit('appNotificationBarSet', { message: 'Broadcast saved!' })

            //  If we failed then do nothing
            if (!response) return

            //  Lets navigate to broadcasts index if it succeeded
            this.$router.push('/broadcasts')
        },
        //  Save the broadcast as a draft
        async saveDraftBroadcast(formClick: boolean = false): Promise<Broadcast> {
            //  Set loading spinner to true
            this.loading = true

            //  If name is not set, set name as auto generated name
            if (!this.temp?.name?.length) this.temp.name = this.autoBroadcastName

            //  If users gets to this method from form click, emit event
            if (formClick) this.$emit('formClick')

            //  Grab temp
            const broadcast = this.temp

            //  Set status to draft
            broadcast.status = 'draft'

            //  Grab our media id
            const media_id = (broadcast.media && broadcast.media.id) || null

            //  Remove media from the broadcast
            delete broadcast.media

            //  Attach media id
            broadcast.media_id = media_id

            //  Attach opt-out message / company name if desired, without this drafts cannot save any change made to the opt-out and company name toggle as they aren't appended to the message directly
            broadcast.message = this.parsedBroadcastMessage

            //  Our method depends on if we are editing or not
            const method = this.edit ? 'draftPut' : 'draftPost'

            //  Send the request
            const response = await this.$store.dispatch(method, broadcast)

            //  If the request failed, do nothing else
            if (!response) return null

            //  Notify the user of success
            this.$store.commit('appNotificationBarSet', { message: 'Broadcast saved!' })

            //  We succeeded so return true
            return response
        },
        //  This gets triggered if someone is just clicking "Save Draft"
        async saveDraft(): Promise<void> {
            //  Emit this to avoid our pop up
            this.$emit('formClick')

            //  Lets save our broadcast
            const success = await this.saveDraftBroadcast()

            //  If we failed then do nothing
            if (!success) return

            //  Lets navigate to broadcasts index if it succeeded
            this.$router.push('/broadcasts')
        },
        //  Update the media in the preview
        updateMedia(media: Media): void {
            //  Update this for our phone preview
            this.broadcastMedia = media

            //  Unset our hint just in case
            this.$store.commit('broadcastsViewMediaHintSet', false)
        },
        //  Update the message in the preview
        updateMessage(message: string): void {
            //  Update for our phone preview
            this.broadcastMessage = message
        },
        //  Update the broadcast name
        updateName(name: string): void {
            this.temp.name = name
        },
        validateSchedules(): boolean {
            //  If schedules do NOT exist, they're not valid, return false
            if (!this.broadcast.schedule) return false

            //  Create an errors container
            const errors = []

            //  Grab our current schedule to check
            const schedule = this.broadcast.schedule

            //  Search for errors in the schedules array
            const v = schedule.next_send !== null && schedule.interval !== null
            if (!v) errors.push(schedule)

            //  Check if errors were found
            const valid = errors.length === 0

            //  Update the validation state with the result
            this.schedulesValid = valid
        }
    },
    watch: {
        //  Watch our broadcast variable
        broadcast: {
            deep: true,
            handler(): void {
                //  Tell our parent to refresh our temp (we do this to handle for draft data)
                this.cloneBroadcast()
            }
        }
    }
})
